import React, { useContext, useEffect, useRef, useState } from "react";
import { Box } from "rizzui/box";
import { Text } from "rizzui/typography";
import FormBox from "./fromBox";
import { Controller, useForm } from "react-hook-form";
import { Input } from "rizzui/input";
import { Button } from "rizzui/button";
import "primereact/resources/themes/lara-light-cyan/theme.css";
import { useModal } from "../../../hooks/useModal";
import ImportVoice from "../../../component/importVoice";
import { inputClassName, labelClassName, textAreaClassName } from "./first";
import Select from "react-select";
import { ThemeContext } from "../../../context/themeContext";
import {
  keepPreviousData,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import axios from "axios";
import { baseUrl } from "../../account/login";
import { AuthContext } from "../../../context/authContext";
import { useNavigate } from "react-router";
import CryptoJS from "crypto-js";
import { useColorStyle } from "../../../hooks/useColorStyle";

export const projectFieldValidation = {
  required: {
    value: true,
    message: "is required",
  },
};

export default function Second({ data }) {
  const { openModal } = useModal();
  const { page, setPage, campaign, setCurrentId } = data;
  const [isPlaying, setIsPlaying] = useState(false);

  const {
    handleSubmit,
    register,
    control,
    getValues,
    reset,
    setValue,
    formState: { errors },
  } = useForm({});

  const { theme } = useContext(ThemeContext);
  const { token } = useContext(AuthContext);
  const AuthStr = "Bearer ".concat(token!);

  const { campaignColorStyles: colorStyles } = useColorStyle(theme);

  const navigate = useNavigate();
  const key = process.env.REACT_APP_SECRET_KEY;
  const [url, setUrl] = useState("");

  const getAgents = () => {
    return axios
      .get(`${baseUrl}/agents`, {
        headers: { Authorization: AuthStr },
      })
      .then((response) => {
        const data = response.data?.data;
        const options = data.map((agent) => {
          const allLabels = Object.values(agent.labels);
          const labels = allLabels
            .map(
              (str: any) =>
                str.charAt(0).toUpperCase() + str.replace("_", " ").slice(1)
            )
            .join(", ");
          return {
            label: `${agent.name} - ${labels}`,
            value: agent.id,
            key: agent.id,
            provider: agent.provider,
            preview_url:
              agent.preview_url ??
              `https://api.elevenlabs.io/v1/voices/${agent.id}/samples/${agent.sample[0]?.sampleId}/audio`,
          };
        });
        return options;
      })
      .catch((e) => {
        if (e?.response?.data?.statusCode === 401) navigate("/account/logout");
        return Promise.reject(
          new Error(e?.message ?? "Check your internet connection")
        );
      });
  };

  const campaign_id = sessionStorage.getItem("campaign_id");
  const queryClient = useQueryClient();

  const finishCampaignAgent = () => {
    if (campaign_id) {
      const { agent_name, agent_role, agent_id, agent_provider } = getValues();
      const id = agent_id?.value;
      return axios
        .post(
          `${baseUrl}/campaigns/${campaign_id}/agents`,
          { agent_name, agent_role, agent_id: id, agent_provider },
          { headers: { Authorization: AuthStr } }
        )
        .then((response) => {
          const data = response.data;
          if (response?.status === 201) {
            const newpage = page + 1;
            const encryptedPage = CryptoJS.AES.encrypt(
              JSON.stringify(newpage),
              key!
            ).toString();
            sessionStorage.setItem("new_campaign_page", encryptedPage);
            setPage(page + 1);
            queryClient.invalidateQueries({ queryKey: [campaign?._id, token] });
          }
          return data;
        })
        .catch((e) => {
          if (e?.response?.data?.statusCode === 401)
            navigate("/account/logout");
          return Promise.reject(
            new Error(e?.message ?? "Check your internet connection")
          );
        });
    } else {
      sessionStorage.removeItem("new_campaign_page");
      return setPage(0);
    }
  };

  const voiceRef = useRef<HTMLAudioElement | null>(null);

  const playAudio = async () => {
    if (url && !isPlaying) {
      const apiKey = process.env.REACT_APP_xi_api_key;
      const isElevenLabs = url.includes("api.elevenlabs.io");
      const response = await axios.get(url, {
        ...(isElevenLabs && { headers: { "xi-api-key": apiKey! } }),
        responseType: "blob",
      });
      const blob = response.data;
      const blobUrl = URL.createObjectURL(blob);
      const voice = new Audio(blobUrl);
      voiceRef.current = voice;
      setIsPlaying(true);
      voice.play();
      voice.onended = () => {
        setIsPlaying(false);
      };
    }
  };

  useEffect(() => {
    return () => {
      if (voiceRef.current) {
        voiceRef.current.pause();
        voiceRef.current.currentTime = 0;
        setIsPlaying(false);
      }
    };
  }, [url]);
  const {
    isLoading,
    isError,
    error,
    data: agents,
  } = useQuery({
    queryKey: ["getAgents", token],
    queryFn: () => getAgents(),
    placeholderData: keepPreviousData,
  });

  const {
    isLoading: agentLoading,
    error: agentError,
    data: response,
    refetch,
  } = useQuery({
    queryKey: ["new_campaign_agent", token, campaign_id],
    queryFn: () => finishCampaignAgent(),
    enabled: false,
    retry: 0,
  });

  const handleNext = handleSubmit((data) => {
    refetch();
  });

  useEffect(() => {
    const agent_id = agents?.find(
      (agent) => agent?.value === campaign?.ai_agent_id
    );
    const agent_name = campaign?.ai_agent_name;
    const agent_role = campaign?.ai_agent_role;
    campaign && reset({ agent_name, agent_role, agent_id });
    agent_id && setUrl(agent_id.preview_url);
  }, [campaign, agents]);

  return (
    <FormBox
      title={"AI agent"}
      subtitle={"Input info about your AI agent"}
      containerClass={
        "w-full text-secondary-dark font-switzer !text-[12px] font-[400] leading-[15.84px]"
      }
      parentClass={"self-start md:w-[460px]"}
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          handleNext();
        }}
        className="flex flex-col gap-[16px]"
        noValidate
      >
        <Input
          type="text"
          label="AI agent name*"
          variant="text"
          rounded="md"
          labelClassName={labelClassName}
          inputClassName={inputClassName}
          {...register("agent_name", {
            ...projectFieldValidation,
          })}
          error={
            errors.agent_name?.message
              ? "AI name " + errors.agent_name.message
              : ""
          }
        />

        <Input
          type="text"
          label="AI agent role*"
          variant="text"
          rounded="md"
          labelClassName={labelClassName}
          inputClassName={inputClassName}
          {...register("agent_role", {
            ...projectFieldValidation,
          })}
          error={
            errors.agent_role?.message
              ? "AI role " + errors.agent_role.message
              : ""
          }
        />

        <Box>
          <div className="justify-between flex mb-1">
            <Text className={labelClassName}>AI voice*</Text>
            <span
              onClick={() => {
                openModal({ view: <ImportVoice /> });
              }}
              className="cursor-pointer"
            >
              <Text className="underline font-switzer font-[500] text-[12px] leading-[15.84px] text-primary-dark">
                Clone your voice
              </Text>
            </span>
          </div>

          <Controller
            control={control}
            name="agent_id"
            rules={{
              required: {
                value: true,
                message: "Required",
              },
            }}
            render={({ field: { onChange, value, name } }) => (
              <div>
                <Select
                  options={agents}
                  placeholder=""
                  styles={colorStyles}
                  isSearchable={false}
                  value={value}
                  name={name}
                  onChange={(e) => {
                    setUrl(e.preview_url);
                    onChange(e);
                    setValue("agent_provider", e.provider);
                  }}
                  isLoading={isLoading}
                />
                {errors.agent_id?.message && (
                  <p className="text-red text-[13px] mt-0.5 rizzui-textarea-error-text">
                    {"" + errors.agent_id.message}
                  </p>
                )}
              </div>
            )}
          />

          <Text className="text-[12px] leading-[15.84px] text-[#758599]">
            <span
              className="underline font-switzer font-[500] cursor-pointer"
              onClick={playAudio}
            >
              Click here{" "}
            </span>{" "}
            to listen to the voice
          </Text>
        </Box>

        <div className="flex justify-between gap-[26px]">
          <Button
            onClick={() => {
              setCurrentId(campaign_id);
              const newpage = page - 1;
              const encryptedPage = CryptoJS.AES.encrypt(
                JSON.stringify(newpage),
                key!
              ).toString();
              sessionStorage.setItem("new_campaign_page", encryptedPage);
              setPage(newpage);
            }}
            className="h-[40px] !bg-[#F2F2F2] w-[119px] dark:!bg-muted dark:!text-[#D9D9D9] !rounded-[4px] font-[500] font-switzer text-[12px] leading-[15.84px] text-[#191919]"
          >
            Go back
          </Button>

          <Button
            type="submit"
            isLoading={agentLoading}
            className="h-[40px] w-[251px] !bg-btn !rounded-[4px] font-[500] font-switzer text-[12px] leading-[15.84px] text-background"
          >
            Continue
          </Button>
        </div>
      </form>
      {agentError && (
        <Text className="text-[red] font-switzer mt-2 text-[12px] font-[400]">
          {agentError.message}
        </Text>
      )}
    </FormBox>
  );
}
